// packages/common/context/src/context.ts
import { log } from "@dxos/log";
import { safeInstanceof } from "@dxos/util";
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Context = class Context1 {
  constructor({ onError = (error) => {
    void this.dispose();
    throw error;
  } } = {}) {
    this._disposeCallbacks = [];
    this._isDisposed = false;
    this._onError = onError;
  }
  get disposed() {
    return this._isDisposed;
  }
  /**
  * Schedules a callback to run when the context is disposed.
  * May be async, in this case the disposer might choose to wait for all resource to released.
  * Throwing an error inside the callback will result in the error being logged, but not re-thrown.
  *
  * NOTE: Will call the callback immediately if the context is already disposed.
  */
  onDispose(callback) {
    if (this._isDisposed) {
      void (async () => {
        try {
          await callback();
        } catch (error) {
          log.catch(error, {}, {
            file: "context.ts",
            line: 52,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
      })();
    }
    this._disposeCallbacks.push(callback);
  }
  /**
  * Runs all dispose callbacks.
  * Sync callbacks are run in the reverse order they were added.
  * Async callbacks are run in parallel.
  * This function never throws.
  * It is safe to ignore the returned promise if the caller does not wish to wait for callbacks to complete.
  * Disposing context means that onDispose will throw an error and any errors raised will be logged and not propagated.
  */
  dispose() {
    if (this._disposePromise) {
      return this._disposePromise;
    }
    this._isDisposed = true;
    const promises = [];
    for (const callback of this._disposeCallbacks.reverse()) {
      promises.push((async () => {
        try {
          await callback();
        } catch (error) {
          log.catch(error, {}, {
            file: "context.ts",
            line: 81,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        }
      })());
    }
    this._disposeCallbacks.length = 0;
    return this._disposePromise = Promise.all(promises).then(() => {
    });
  }
  /**
  * Raise the error inside the context.
  * The error will be propagated to the error handler.
  * IF the error handler is not set, the error will dispose the context and cause an unhandled rejection.
  */
  raise(error) {
    if (this._isDisposed) {
      log.warn("Error in disposed context", error, {
        file: "context.ts",
        line: 98,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      return;
    }
    try {
      this._onError(error);
    } catch (err) {
      void Promise.reject(err);
    }
  }
  derive({ onError } = {}) {
    const newCtx = new Context({
      onError: async (error) => {
        if (!onError) {
          this.raise(error);
        } else {
          try {
            await onError(error);
          } catch (e) {
            this.raise(error);
          }
        }
      }
    });
    this.onDispose(() => newCtx.dispose());
    return newCtx;
  }
};
Context = __decorate([
  safeInstanceof("Context")
], Context);

// packages/common/context/src/promise-utils.ts
import { CancelledError } from "@dxos/errors";
var rejectOnDispose = (ctx, error = new CancelledError()) => new Promise((resolve, reject) => {
  ctx.onDispose(() => reject(error));
});
var cancelWithContext = (ctx, promise) => {
  return Promise.race([
    promise,
    rejectOnDispose(ctx)
  ]);
};
export {
  Context,
  cancelWithContext,
  rejectOnDispose
};
//# sourceMappingURL=index.mjs.map
